import type { ElevationProfile, ProfileSample } from '@/atoms/elevationProfile.atom';

/**
 * Persistence for a project's elevation profiles.
 *
 * Why IndexedDB and not localStorage: a profile carries up to 5 000 samples,
 * and as JSON in localStorage that is ~250 KB of UTF-16 per profile against a
 * ~5 MB quota shared by every project and every other setting on the origin.
 * A few dozen profiles filled it, after which every Compute threw the
 * browser's raw "The quota has been exceeded." Here the samples are one
 * interleaved `Float64Array` (16 bytes a sample, ~6× smaller) and the quota is
 * a share of the disk.
 *
 * Profiles saved by the old `atomWithStorage` version are moved over on the
 * first load of their project and their localStorage key removed.
 */

/** Interleaved `[chainage0, elevation0, chainage1, elevation1, …]`. */
type StoredProfile = Omit<ElevationProfile, 'samples'> & { samples: Float64Array };

/** The key/value surface the store needs; IndexedDB in the app, a Map in tests. */
export interface ProfileBackend {
  get(projectId: string): Promise<StoredProfile[] | undefined>;
  put(projectId: string, profiles: StoredProfile[]): Promise<void>;
  delete(projectId: string): Promise<void>;
}

export const legacyStorageKey = (projectId: string) => `@actegon.gis.elevationProfiles.${projectId}`;

export function encodeSamples(samples: ReadonlyArray<ProfileSample>): Float64Array {
  const out = new Float64Array(samples.length * 2);
  for (let i = 0; i < samples.length; i++) {
    out[2 * i] = samples[i].chainage;
    out[2 * i + 1] = samples[i].elevation;
  }
  return out;
}

export function decodeSamples(packed: Float64Array | ReadonlyArray<ProfileSample> | undefined): ProfileSample[] {
  if (!packed) return [];
  // `isView`, not `instanceof`: a structured clone can come from another realm.
  if (!ArrayBuffer.isView(packed)) return [...packed];
  const out: ProfileSample[] = new Array(packed.length >>> 1);
  for (let i = 0; i < out.length; i++) {
    out[i] = { chainage: packed[2 * i], elevation: packed[2 * i + 1] };
  }
  return out;
}

const toStored = (p: ElevationProfile): StoredProfile => ({ ...p, samples: encodeSamples(p.samples) });
const fromStored = (p: StoredProfile): ElevationProfile => ({ ...p, samples: decodeSamples(p.samples) });

/**
 * Stored profiles with the ones changed in memory before the load finished
 * laid over them: same id → the in-memory one wins, new ids are appended.
 */
export function mergeLoaded(
  stored: ReadonlyArray<ElevationProfile>,
  current: ReadonlyArray<ElevationProfile>,
): ElevationProfile[] {
  const currentIds = new Set(current.map((p) => p.id));
  return [...stored.filter((p) => !currentIds.has(p.id)), ...current];
}

const DB_NAME = 'actegon.gis';
const DB_VERSION = 1;
const STORE = 'elevationProfiles';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('IndexedDB is not available'));
        return;
      }
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      req.onblocked = () => reject(new Error('IndexedDB open blocked'));
    });
    // A failed open is not cached, so the next call can try again.
    dbPromise.catch(() => {
      dbPromise = null;
    });
  }
  return dbPromise;
}

function request<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const req = run(tx.objectStore(STORE));
        // Resolve on commit, not on the request's success: a write isn't
        // durable (and a quota error isn't raised) until the transaction ends.
        tx.oncomplete = () => resolve(req.result as T);
        tx.onerror = () => reject(tx.error ?? req.error);
        tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
      }),
  );
}

export const indexedDbBackend: ProfileBackend = {
  get: (projectId) => request('readonly', (s) => s.get(projectId)),
  put: (projectId, profiles) => request('readwrite', (s) => s.put(profiles, projectId)),
  delete: (projectId) => request('readwrite', (s) => s.delete(projectId)),
};

function readLegacy(projectId: string): ElevationProfile[] | null {
  try {
    const raw = localStorage.getItem(legacyStorageKey(projectId));
    if (raw == null) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ElevationProfile[]) : null;
  } catch {
    return null;
  }
}

function removeLegacy(projectId: string) {
  try {
    localStorage.removeItem(legacyStorageKey(projectId));
  } catch {
    // Nothing to free if storage is inaccessible.
  }
}

/**
 * The project's saved profiles. Rejects only when the backend can't be read,
 * so the caller knows not to write over what may be there.
 */
export async function loadProfiles(
  projectId: string,
  backend: ProfileBackend = indexedDbBackend,
): Promise<ElevationProfile[]> {
  const stored = await backend.get(projectId);
  if (stored) {
    // A put that landed before a crash may have left the old key behind.
    removeLegacy(projectId);
    return stored.map(fromStored);
  }
  const legacy = readLegacy(projectId);
  if (!legacy) return [];
  await backend.put(projectId, legacy.map(toStored));
  removeLegacy(projectId);
  return legacy.map((p) => ({ ...p, samples: p.samples ?? [] }));
}

export async function saveProfiles(
  projectId: string,
  profiles: ReadonlyArray<ElevationProfile>,
  backend: ProfileBackend = indexedDbBackend,
): Promise<void> {
  if (profiles.length === 0) await backend.delete(projectId);
  else await backend.put(projectId, profiles.map(toStored));
}
